<template>
    <div>
        <modal name="modal-add-client" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-10 overflow-y-auto max-h-[100vh]" style="z-index: 999 !important;">
                <div class="flex items-center justify-between mb-4 pb-4 border-b text-2xl">
                    <h2 class="font-bold">
                        Ajouter un client
                    </h2>
                    <button @click="$modal.hide('modal-add-client');">
                        <i class="fas fa-times text-red-500"></i>
                    </button>
                </div>
                <client-form v-model="newClient" />
                <div class="flex justify-center mt-6">
                    <button @click="addClient" class="bg-gradient-to-r from-red-600 to-orange-500 text-white font-medium px-4 py-2 rounded-lg shadow transition flex items-center gap-2">
                        <i class="fas fa-check"></i> Enregistrer le client
                    </button>
                </div>
            </div>
        </modal>

        <app-template>
            <section class="space-y-8">

                <!-- Header -->
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h2 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            Clients
                        </h2>
                        <p class="text-sm text-gray-500 mt-1">Gérez vos clients particuliers et professionnels.</p>
                    </div>
                    <button @click="$modal.show('modal-add-client')"
                        class="bg-gradient-to-r from-red-600 to-orange-500 text-white font-medium px-4 py-2 rounded-lg shadow transition flex items-center gap-2">
                        <i class="fas fa-plus"></i> Ajouter un client
                    </button>
                </div>

                <!-- Filtres -->
                <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div class="col-span-3">
                            <label class="text-sm font-medium text-gray-700">Rechercher</label>
                            <div class="relative mt-1">
                                <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
                                <input v-model="filters.search" type="text"
                                    placeholder="Nom, société, email, téléphone..."
                                    class="pl-9 w-full rounded-lg border border-gray-300 focus:outline-none text-sm py-2">
                            </div>
                        </div>

                        <div>
                            <label class="text-sm font-medium text-gray-700">Type de client</label>
                            <div class="relative mt-1">
                                <i class="fas fa-user-tag absolute left-3 top-3 text-gray-400"></i>
                                <select v-model="filters.type_client"
                                    class="pl-9 w-full rounded-lg border border-gray-300 focus:outline-none text-sm py-2">
                                    <option value="">Tous</option>
                                    <option value="Particulier">Particulier</option>
                                    <option value="Professionnel">Professionnel</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tableau -->
                <div class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden relative">
                    <table class="w-full text-sm text-left">
                        <thead class="bg-gray-100 text-gray-600 uppercase text-xs font-semibold">
                            <tr>
                                <th class="px-6 py-3">Nom / Société</th>
                                <th class="px-6 py-3">Type</th>
                                <th class="px-6 py-3">Téléphone</th>
                                <th class="px-6 py-3">Email</th>
                                <th class="px-6 py-3">Ville</th>
                                <th class="px-6 py-3">Dernière intervention</th>
                            </tr>
                        </thead>

                        <!-- Skeleton loader -->
                        <tbody v-if="loading" class="divide-y divide-gray-100">
                            <tr v-for="n in 1" :key="n">
                                <td v-for="i in 6" :key="i" class="px-6 py-3">
                                    <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                                </td>
                            </tr>
                        </tbody>

                        <!-- Données réelles -->
                        <tbody v-else class="divide-y divide-gray-100">
                            <tr v-for="client in clients.data" :key="client.id"
                                @click="$router.push({ name: 'ClientView', params: { id: client.id } })"
                                class="hover:bg-blue-100 cursor-pointer transition">
                                <td class="px-6 py-3 font-bold">{{ client.nom }}</td>
                                <td class="px-6 py-3">{{ client.type_client || '-' }}</td>
                                <td class="px-6 py-3">{{ client.telephone || '-' }}</td>
                                <td class="px-6 py-3">{{ client.email }}</td>
                                <td class="px-6 py-3">{{ client.ville || '-' }}</td>
                                <td class="px-6 py-3 text-gray-500">—</td>
                            </tr>
                            <tr v-if="!clients.data.length">
                                <td colspan="6" class="px-6 py-4 text-center text-gray-400 italic">
                                    Aucun client trouvé
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div class="flex justify-between items-center text-sm text-gray-600 mt-4">
                    <p>
                        Affichage de {{ clients.from || 0 }} à {{ clients.to || 0 }}
                        sur {{ clients.total || 0 }} clients
                    </p>

                    <div class="flex gap-1">
                        <button :disabled="!clients.prev_page_url" @click="fetchClients(clients.current_page - 1)"
                            class="px-3 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 flex items-center gap-1 disabled:opacity-50">
                            <i class="fas fa-chevron-left text-xs"></i> Précédent
                        </button>

                        <span v-for="page in pages" :key="page" @click="fetchClients(page)"
                            :class="[
                                'px-3 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 cursor-pointer',
                                page === clients.current_page ? 'font-semibold text-blue-600 border-blue-400' : ''
                              ]">
                            {{ page }}
                        </span>

                        <button :disabled="!clients.next_page_url" @click="fetchClients(clients.current_page + 1)"
                            class="px-3 py-1 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 flex items-center gap-1 disabled:opacity-50">
                            Suivant <i class="fas fa-chevron-right text-xs"></i>
                        </button>
                    </div>
                </div>

            </section>
        </app-template>
    </div>
</template>

<script>
import ClientForm from './Components/ClientForm.vue'

export default {
    name: "ClientList",
    components: {
        ClientForm
    },
    data() {
        return {
            clients: {
                data: [],
                current_page: 1,
                total: 0,
                from: 0,
                to: 0
            },
            filters: {
                search: '',
                type_client: ''
            },
            searchTimeout: null,
            loading: false,
            pages: [],

            newClient: {}
        }
    },
    watch: {
        'filters.search': function () {
            this.debouncedFetch()
        },
        'filters.type_client': function () {
            this.debouncedFetch()
        }
    },
    methods: {
        async fetchClients(page = 1) {
            this.loading = true
            try {
                const params = {
                    page,
                    search: this.filters.search || '',
                    type_client: this.filters.type_client || '',
                    type: 'proprietaire'
                }
                const res = await this.$request.get('/clients', { params })
                this.clients = res
                this.generatePagination()
            } catch (e) {
                this.$toast.error('Erreur lors du chargement des clients')
            } finally {
                // petit délai pour lisser la transition visuelle
                setTimeout(() => { this.loading = false }, 250)
            }
        },
        debouncedFetch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.fetchClients()
            }, 400)
        },
        generatePagination() {
            if (!this.clients.last_page) return
            const totalPages = this.clients.last_page
            const current = this.clients.current_page
            const pages = []
            for (let i = Math.max(1, current - 2); i <= Math.min(totalPages, current + 2); i++) {
                pages.push(i)
            }
            this.pages = pages
        },

        async addClient() {
            const type = this.newClient?.proprietaire?.type_client || 'particulier'

            const rules = type === 'professionnel'
                ? [
                    'proprietaire-nom:required|min=2',
                    'proprietaire-siret:required|siret',
                ]
                : [
                    'proprietaire-nom:required|min=2',
                    'proprietaire-email:required|email',
                    'proprietaire-telephone:required|tel',
                ]

            const validation = this.$inpError(rules)
            if (!validation) {
                this.$toast.error('Formulaire invalide')
                return
            }
            try {
                await this.$request.post(`/clients`, this.newClient)
                await this.fetchClients()
                this.$toast.success('Client ajouté')
                this.$modal.hide('modal-add-client')
                this.newClient = {}
            } catch (e) {
                this.$toast.error('Erreur lors de l\'ajout du client')
            }
        },
    },

    async mounted() {
        await this.fetchClients()
    },
}
</script>

<style scoped>
button[disabled] {
    cursor: not-allowed;
    opacity: 0.5;
}
</style>
